import React, { FC, useRef } from "react"
import type { KeyboardShortcut } from "resha"
import { ButtonGroup, OutlinedButton, Footer } from "dash-styled"
import { ReactComponent as ReloadIcon } from "icons/lib/reload.svg"
import { ReactComponent as PenIcon } from "icons/lib/pen.svg"
import { ReactComponent as CheckIcon } from "icons/lib/check.svg"
import { ReactComponent as CancelIcon } from "icons/lib/close_octagon.svg"
import { ReactComponent as DeleteIcon } from "icons/lib/trash.svg"

import { APIJobModel } from "../../../../../../../Models/API/JobModel"
import { useAsyncCommand } from "../../../../../../../Hooks/useAsyncCommand"
import { TagsViewEdit } from "../TagsViewEdit/TagsViewEdit"
import { ShortcutView } from "../Shortcut/ShortcutView"

export const DetailViewFooter: FC<{
    appID: string
    job?: APIJobModel
    correct?: boolean | null
    isTaskView: boolean
    positiveLabel: string
    positiveOngoingLabel: string
    positiveClassName?: string
    negativeLabel: string
    negativeOngoingLabel: string
    negativeClassName?: string
    enablePositiveAction: boolean
    enableNegativeAction: boolean
    isReviewView: boolean
    shortcutList?: { [key: string]: [string, KeyboardShortcut][] }
    highlightEdit?: boolean
    onEdit?: () => void
    onRelabelRequest?: () => void
    onDelete?: () => void
    onMessages?: () => void
    onAction: (correct: boolean, message?: string) => Promise<void>
    onAddTag?: (value: string, isNew: boolean) => void
    onRemoveTag?: (value: string) => void
}> = ({
    appID,
    job,
    correct,
    isTaskView,
    positiveLabel,
    positiveOngoingLabel,
    positiveClassName,
    negativeLabel,
    negativeOngoingLabel,
    negativeClassName,
    enablePositiveAction,
    isReviewView,
    shortcutList,
    highlightEdit,
    onEdit,
    onRelabelRequest,
    onDelete,
    onMessages,
    onAction,
    onAddTag,
    onRemoveTag
}) => {
    const ref = useRef<HTMLDivElement>(null)

    const markAsPositive = useAsyncCommand((message?: string) =>
        onAction(true, message)
    )

    const markAsNegative = useAsyncCommand((message?: string) =>
        onAction(false, message)
    )

    return (
        <Footer
            text={isTaskView ? "Task Review" : "Data Review"}
            actions={
                <>
                    {!isTaskView && (
                        <TagsViewEdit
                            appID={appID}
                            tags={job?.tags || []}
                            onAdd={onAddTag}
                            onRemove={onRemoveTag}
                        />
                    )}
                    {onEdit && (
                        <OutlinedButton
                            Icon={PenIcon}
                            marginRight="2"
                            label="Edit Job"
                            onClick={onEdit}
                            color={highlightEdit ? "violet" : "plain"}
                        />
                    )}
                    {onRelabelRequest && (
                        <OutlinedButton
                            Icon={ReloadIcon}
                            marginRight="2"
                            onClick={onRelabelRequest}
                            label="Reprocess"
                        />
                    )}
                    {isReviewView && shortcutList !== undefined ? (
                        <ShortcutView shortcutList={shortcutList} />
                    ) : null}
                    {onDelete && (
                        <OutlinedButton
                            Icon={DeleteIcon}
                            marginRight="2"
                            label="Delete"
                            onClick={onDelete}
                        />
                    )}
                </>
            }
        >
            <ButtonGroup equalWidth ref={ref}>
                <OutlinedButton
                    className={`opacity-100 ${negativeClassName ?? ""}`}
                    color={correct === false ? "negative" : "plain"}
                    Icon={CancelIcon}
                    label={
                        markAsNegative.isProcessing
                            ? negativeOngoingLabel
                            : negativeLabel
                    }
                    onClick={markAsNegative.perform}
                    disabled={
                        markAsNegative.perform === undefined ||
                        markAsNegative.isProcessing
                    }
                />
                <OutlinedButton
                    className={`opacity-100 ${positiveClassName ?? ""}`}
                    color={
                        isReviewView
                            ? enablePositiveAction
                                ? "violet"
                                : "plain"
                            : correct === true
                            ? "positive"
                            : "plain"
                    }
                    Icon={CheckIcon}
                    label={
                        markAsPositive.isProcessing
                            ? positiveOngoingLabel
                            : positiveLabel
                    }
                    onClick={markAsPositive.perform}
                    disabled={
                        markAsPositive.perform === undefined ||
                        markAsPositive.isProcessing ||
                        !enablePositiveAction
                    }
                />
            </ButtonGroup>
        </Footer>
    )
}
